import { z } from "zod";
import { fetchWithInterceptor } from "./client";
import { API_BASE, handleResponse, parseResponse } from "./common";
import {
  AttachmentDtoSchema,
  type AttachmentDto,
  type AttachmentType,
  type UpdateAttachmentDto,
} from "./types";

export const getAttachments = async (): Promise<AttachmentDto[]> => {
  const response = await fetchWithInterceptor(`${API_BASE}/attachments`);
  await handleResponse(response);
  return parseResponse(
    z.array(AttachmentDtoSchema),
    await response.json(),
    "GET /attachments",
  );
};

export const getPendingAttachments = async (): Promise<AttachmentDto[]> => {
  const response = await fetchWithInterceptor(
    `${API_BASE}/attachments?pending=true`,
  );
  await handleResponse(response);
  return parseResponse(
    z.array(AttachmentDtoSchema),
    await response.json(),
    "GET /attachments?pending=true",
  );
};

export const deleteAttachment = async (id: string): Promise<void> => {
  const response = await fetchWithInterceptor(`${API_BASE}/attachments/${id}`, {
    method: "DELETE",
  });
  await handleResponse(response);
};

export const updateAttachment = async (
  id: string,
  body: UpdateAttachmentDto,
): Promise<AttachmentDto> => {
  const response = await fetchWithInterceptor(`${API_BASE}/attachments/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  await handleResponse(response);
  return parseResponse(
    AttachmentDtoSchema,
    await response.json(),
    "PUT /attachments/:id",
  );
};

export const uploadAttachment = async (
  file: File | Blob,
  filename: string,
  contentType: AttachmentType,
  preview?: Blob,
): Promise<AttachmentDto> => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("filename", filename);
  formData.append("contentType", contentType);

  if (preview) {
    formData.append("preview", preview);
  }

  const response = await fetchWithInterceptor(`${API_BASE}/attachments`, {
    method: "POST",
    body: formData,
  });

  await handleResponse(response);
  return parseResponse(
    AttachmentDtoSchema,
    await response.json(),
    "POST /attachments",
  );
};

/**
 * Replace an existing attachment's file **in place**, keeping its id.
 *
 * Used when a stored PDF turns out to be an unreadable copy and the user
 * supplies the original: invoices and transactions already reference this id,
 * so uploading a new attachment and deleting the old one would break those
 * links. Same id in, same id out.
 */
export const replaceAttachmentContent = async (
  id: string,
  file: File | Blob,
  filename: string,
  contentType: AttachmentType,
  preview?: Blob,
): Promise<AttachmentDto> => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("filename", filename);
  formData.append("contentType", contentType);

  if (preview) {
    formData.append("preview", preview);
  }

  const response = await fetchWithInterceptor(
    `${API_BASE}/attachments/${id}/content`,
    { method: "PUT", body: formData },
  );

  await handleResponse(response);
  return parseResponse(
    AttachmentDtoSchema,
    await response.json(),
    "PUT /attachments/:id/content",
  );
};

/**
 * URL of an attachment's file — the one place it is built.
 *
 * The backend serves a versioned URL `immutable`, and replace-with-original
 * overwrites the file under the same id. `previewVersion` is bumped by every
 * such replacement, so it doubles as the content version: a replaced file gets
 * a new URL instead of the browser's year-old cached copy. (A preview-only
 * change bumps it too; that costs one re-download, never a stale file.)
 */
export const attachmentContentUrl = (
  attachment: Pick<AttachmentDto, "id" | "previewVersion">,
): string =>
  `${API_BASE}/attachments/${attachment.id}?v=${attachment.previewVersion}`;

/** Download the raw attachment file (e.g. a PDF) as a Blob. */
export const getAttachmentBlob = async (
  attachment: Pick<AttachmentDto, "id" | "previewVersion">,
): Promise<Blob> => {
  const response = await fetchWithInterceptor(attachmentContentUrl(attachment));
  await handleResponse(response);
  return response.blob();
};

export const getAttachmentPreviewUrl = (id: string): string => {
  return `/api/attachments/${id}/preview`;
};

/**
 * Cache-busted preview URL, or `undefined` when there is nothing to show.
 *
 * `previewVersion` only leaves 0 once a preview blob is written, and the upload
 * endpoint treats the `preview` part as optional — so a PDF uploaded outside
 * the browser (API clients, the seed script) never gets one, and requesting it
 * would only 404.
 *
 * An image is different: the endpoint falls back to the original file when no
 * preview blob exists, so an image always has a URL regardless of version.
 */
export const attachmentPreviewSrc = (
  attachment: Pick<AttachmentDto, "id" | "previewVersion" | "contentType">,
): string | undefined =>
  attachment.previewVersion > 0 || attachment.contentType === "image/webp"
    ? `${getAttachmentPreviewUrl(attachment.id)}?v=${attachment.previewVersion}`
    : undefined;

export const updateAttachmentPreview = async (
  id: string,
  preview: Blob,
): Promise<AttachmentDto> => {
  const formData = new FormData();
  formData.append("preview", preview);

  const response = await fetchWithInterceptor(
    `${API_BASE}/attachments/${id}/preview`,
    {
      method: "PUT",
      body: formData,
    },
  );
  await handleResponse(response);
  return parseResponse(
    AttachmentDtoSchema,
    await response.json(),
    "PUT /attachments/:id/preview",
  );
};
